import { AutoComplete, Button, Drawer, Input, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { SearchResultDto } from "@audit-plan/shared";
import { api } from "../api/client";
import { palette } from "../theme";
import { useIsMobile } from "./useIsMobile";

export function GlobalSearch() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const mobile = useIsMobile();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const r = useQuery({ queryKey: ["search", q], queryFn: () => api.get<SearchResultDto>("/search", { q }), enabled: q.trim().length >= 2 });
  const d = r.data;
  const groups = d
    ? [
        { label: t("suppliers"), options: d.suppliers.map((s) => ({ value: `/suppliers/${s.id}`, label: `${s.name} · ${s.code}` })) },
        { label: t("audits"), options: d.audits.map((a) => ({ value: `/audits/${a.id}`, label: `${a.code} · ${a.supplierName}` })) },
        { label: t("ncs"), options: d.ncs.map((n) => ({ value: `/ncs?id=${n.id}`, label: `${n.code} · ${n.title}` })) },
        { label: t("actions"), options: d.actions.map((x) => ({ value: `/actions?id=${x.id}`, label: `${x.code} · ${x.title}` })) },
      ].filter((g) => g.options.length)
    : [];
  const go = (v: string) => {
    setQ("");
    setOpen(false);
    nav(v);
  };

  // Smartphone: icona lente che apre la ricerca a tutta larghezza in un pannello dall'alto.
  if (mobile) {
    const ready = q.trim().length >= 2;
    return (
      <>
        <Button type="text" icon={<SearchOutlined />} aria-label={t("m_openSearch")} onClick={() => setOpen(true)} />
        <Drawer
          open={open}
          onClose={() => setOpen(false)}
          placement="top"
          height="auto"
          closable={false}
          styles={{ body: { padding: 12, maxHeight: "80dvh", overflow: "auto" }, header: { display: "none" } }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            <Input autoFocus allowClear size="large" prefix={<SearchOutlined />} placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} style={{ fontSize: 16 }} />
            <Button size="large" onClick={() => setOpen(false)}>
              {t("close")}
            </Button>
          </div>
          {ready && r.isFetching && <Spin style={{ display: "block", margin: 16 }} />}
          {ready && d && !r.isFetching && !groups.length && <div style={{ padding: 16, color: palette.muted, textAlign: "center" }}>{t("noResults")}</div>}
          {groups.map((g) => (
            <div key={g.label} style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700, color: palette.muted, marginBottom: 4 }}>{g.label}</div>
              {g.options.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => go(o.value)}
                  style={{ display: "block", width: "100%", textAlign: "left", font: "inherit", background: "none", border: 0, borderBottom: `1px solid ${palette.hair}`, padding: "10px 4px", minHeight: 44, cursor: "pointer", overflowWrap: "anywhere", color: palette.fg }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          ))}
        </Drawer>
      </>
    );
  }

  return (
    <AutoComplete
      style={{ width: "100%" }}
      options={groups}
      value={q}
      onChange={setQ}
      onSelect={(v: string) => go(v)}
      notFoundContent={q.trim().length >= 2 && d ? t("noResults") : null}
    >
      <Input prefix={<SearchOutlined />} placeholder={t("search")} allowClear />
    </AutoComplete>
  );
}
